import { MediaItemType } from "@questmate/common";

export interface BaseFile {
  /**
   * Random UUID.
   */
  id: string;
  /**
   * URL for the file. Can be an external URL for saved files or a data url for unsaved files.
   */
  src: string;
  alt?: string;
}

export type WebFile = File;
export type NativeFile = {
  uri: string;
  name: string;
  type: string | undefined;
};

export type MediaFile = WebFile | NativeFile;

export interface QueuedFile extends BaseFile {
  state: "QUEUED";
  name: string;
  type: MediaItemType;
  file: MediaFile;
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace FileUpload {
  interface BaseFileUpload extends BaseFile {
    type: MediaItemType;
  }

  export interface Preparing extends BaseFileUpload {
    state: "PREPARING";
    name: string;
    type: MediaItemType;
    file: MediaFile;
  }

  export interface Uploading extends Omit<Preparing, "state"> {
    state: "UPLOADING";
    assemblyId: string;
    uploadProgress: number;
  }

  export interface Ready extends Omit<Uploading, "state"> {
    identifier: string;
    originalBasename: string;
    extension: string;
    mime: string;
    state: "READY_TO_SAVE";
  }

  export interface Error
    extends Partial<Omit<Ready, "state" | keyof BaseFileUpload>>,
      BaseFileUpload {
    state: "ERROR";
    message: string;
    errorData: {
      error: { name: string; message: string; details?: unknown };
    };
  }
}

/**
 * Represents an file that has not yet been persisted in the database.
 * It may be at various stages of being uploaded and is ready to be saved/persisted when in the READY_TO_SAVE state.
 */
export type UploadFile =
  | FileUpload.Preparing
  | FileUpload.Uploading
  | FileUpload.Ready
  | FileUpload.Error;

/**
 * Represents an file that has been persisted in the database and is sourced externally.
 */
export interface SavedFile extends BaseFile {
  state: "SAVED";
  identifier: string;
  name: string;
  originalBasename: string;
  extension: string;
  mime: string;
  type: MediaItemType;
  signedUrl: string;
  signedPreviewUrl: string;
}

export type QMFile = SavedFile | QueuedFile | UploadFile;

export type MinimalSavedFile = Omit<SavedFile, "id" | "src">;
export type MinimalQueuedFile = { state: "QUEUED"; name: string };
export type MinimalUploadingFile = {
  state: Exclude<UploadFile["state"], "READY_TO_SAVE">;
};
export type MinimalReadyToSaveFile = {
  state: "READY_TO_SAVE";
  identifier: string;
  originalBasename: string;
  extension: string;
  mime: string;
  type: MediaItemType;
};
export type MinimalFile =
  | MinimalSavedFile
  | MinimalQueuedFile
  | MinimalUploadingFile
  | MinimalReadyToSaveFile;

export type PersistableFile = Pick<
  SavedFile,
  "identifier" | "originalBasename" | "extension" | "mime" | "type"
>;

export type PersistedFileFromServer = PersistableFile &
  Pick<SavedFile, "signedUrl" | "signedPreviewUrl">;

export function fileIsLoading(file: QMFile | MinimalFile): boolean {
  return ["QUEUED", "PREPARING", "UPLOADING", "ASSEMBLING"].includes(
    file.state
  );
}

export function fileIsError(file: QMFile | MinimalFile): boolean {
  // return true;
  return file.state === "ERROR";
}

export function fileIsSaved(file: QMFile | MinimalFile): boolean {
  return ["SAVED"].includes(file.state);
}
export function fileIsReadyToSave(file: QMFile | MinimalFile): boolean {
  return ["READY_TO_SAVE"].includes(file.state);
}

export function getPersistableFileFields(
  file: MinimalSavedFile | SavedFile | MinimalReadyToSaveFile
): PersistableFile {
  return {
    identifier: file.identifier,
    originalBasename: file.originalBasename,
    extension: file.extension,
    mime: file.mime,
    type: file.type,
  };
}
